import PolicyPage from '../components/PolicyPage';

export default function Privacy() {
  return (
    <PolicyPage
      title="Privacy Policy"
      subtitle="How BharatCart collects, uses and protects your personal data — DPDP Act 2023 compliant."
      lastUpdated="15 June 2026"
      sections={[
        { id: 'introduction', heading: 'Introduction', body: (
          <p>This Privacy Policy explains how <b>BharatCart Pvt. Ltd.</b> ("we", "us") collects, uses, discloses and safeguards your personal data when you use our Platform. It complies with India's <b>Digital Personal Data Protection Act, 2023 (DPDP Act)</b>, the IT Act 2000 and its Reasonable Security Practices rules.</p>
        )},
        { id: 'data-we-collect', heading: 'Data We Collect', body: (
          <>
            <p><b>You provide us:</b></p>
            <ul className="list-disc list-inside space-y-1">
              <li>Name, mobile number, delivery address, email (optional)</li>
              <li>Store name, GSTIN, PAN, FSSAI licence (for shop owners only)</li>
              <li>Photographs and descriptions of products you list</li>
              <li>Order history, saved carts, product reviews</li>
            </ul>
            <p className="mt-2"><b>Collected automatically:</b> device / browser type, IP address, coarse geolocation (for the 5-km nearest-shop feature — never fine-grained), pages visited, timestamps, referring URL.</p>
            <p><b>From third parties:</b> KYC verification results (Aadhaar/PAN authentication providers), payment status from Razorpay / Stripe / PayPal.</p>
          </>
        )},
        { id: 'purpose', heading: 'Purpose of Processing', body: (
          <ul className="list-disc list-inside space-y-1">
            <li>To create your account, authenticate you and let you shop or sell</li>
            <li>To route orders, coordinate delivery and process payments</li>
            <li>To send order updates, price-drop alerts, delivery OTPs and account notifications via SMS / WhatsApp / email</li>
            <li>To personalise product recommendations and nearest-shop discovery</li>
            <li>To detect fraud, prevent abuse, comply with tax and legal obligations</li>
            <li>Improving the Platform via anonymised analytics</li>
          </ul>
        )},
        { id: 'legal-basis', heading: 'Legal Basis (DPDP Act §7)', body: (
          <p>We process your data on the basis of (i) your <b>consent</b> given at signup and at each specific opt-in (e.g. price-drop SMS/WhatsApp), (ii) the <b>necessity to perform a contract</b> (delivering the order you placed), and (iii) our <b>legitimate uses</b> such as fraud prevention and record-keeping under Indian tax law.</p>
        )},
        { id: 'sharing', heading: 'When We Share Your Data', body: (
          <>
            <p>BharatCart never sells your personal data. We share only what is strictly needed:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>The shop owner fulfilling your order (name, address, phone)</li>
              <li>Delivery partners assigned to that order</li>
              <li>Payment processors (Razorpay, Stripe, PayPal) for the specific transaction</li>
              <li>KYC providers when you complete seller onboarding</li>
              <li>Government authorities when compelled by a valid legal order</li>
            </ul>
          </>
        )},
        { id: 'storage', heading: 'Storage, Security & Retention', body: (
          <>
            <p>Data is stored in secure, encrypted-at-rest MongoDB instances hosted in India. Passwords are never stored; we use OTP-only authentication. Card data never touches BharatCart servers.</p>
            <p>We retain personal data only for as long as your account is active plus the period required by law (typically 8 years for GST-invoice records). You may request earlier deletion — see "Your Rights" below.</p>
          </>
        )},
        { id: 'rights', heading: 'Your Rights (DPDP Act §11–14)', body: (
          <>
            <p>You have the right to:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><b>Access</b> a copy of the data we hold about you</li>
              <li><b>Correct or update</b> inaccurate data</li>
              <li><b>Withdraw consent</b> (this will stop marketing but may limit service)</li>
              <li><b>Nominate</b> another person to exercise your rights in case of incapacity</li>
              <li><b>Erasure</b> of your data (subject to legal retention obligations)</li>
              <li><b>Grievance redressal</b> — see contact below</li>
            </ul>
            <p>To exercise any of these, email <a href="mailto:dpo@bharatcart.org" className="text-[#0D47A1] hover:underline">dpo@bharatcart.org</a>. We respond within 30 days.</p>
          </>
        )},
        { id: 'cookies', heading: 'Cookies & Local Storage', body: (
          <p>We use browser <b>local storage</b> to keep you logged in and hold your cart, and <b>session cookies</b> for security. No third-party advertising trackers are set. You may clear these anytime from your browser settings.</p>
        )},
        { id: 'children', heading: 'Children\'s Data', body: (
          <p>BharatCart is not directed at children under 18. If you believe a child has provided us data, contact <a href="mailto:dpo@bharatcart.org" className="text-[#0D47A1] hover:underline">dpo@bharatcart.org</a> and we will erase it.</p>
        )},
        { id: 'grievance', heading: 'Grievance Officer', body: (
          <>
            <p>Under Rule 5(9) of the IT (Reasonable Security Practices) Rules, our Grievance Officer is:</p>
            <p className="mt-2"><b>Ms. R. Sharma</b><br/>BharatCart Pvt. Ltd.<br/>Hyderabad, Telangana, 500034, India<br/>Email: <a href="mailto:grievance@bharatcart.org" className="text-[#0D47A1] hover:underline">grievance@bharatcart.org</a><br/>Phone: <a href="tel:+911800000000" className="text-[#0D47A1] hover:underline">+91 1800-000-000</a></p>
          </>
        )},
        { id: 'changes', heading: 'Changes', body: (
          <p>We will notify you of material changes via in-app banner and email at least 15 days before they take effect.</p>
        )},
      ]}
    />
  );
}
